import { getBoardsListByIds } from "@/services";
import isEditingElement from "@/utils/isEditingElement";
import {
  useKeyPress,
  useMemoizedFn,
  useSetState,
  useUpdateEffect,
} from "ahooks";
import { useCallback, useRef, useState } from "react";
import { Else, If, Then } from "react-if";
import CreateBoard from "../CreateBoard";
import styles from "./index.module.less";
import PinCreatorBoardCard from "./PinCreatorBoardCard";

// 虚拟列表渲染个数
const RENDER_COUNT = 30;
// item高度
const ITEM_HEIGHT = 50;

export default function PinCreatorBoardList({
  keyword,
  dataSource,
  onSelectBoard,
}) {
  const [renderIndex, setRenderIndex] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef(null);

  const onScroll = useCallback((evt) => {
    const scrollTop = evt.target.scrollTop;
    // 计算渲染起始位置
    const index = Math.floor((scrollTop - 10 * ITEM_HEIGHT) / ITEM_HEIGHT);
    setRenderIndex(index < 0 ? 0 : index);
  }, []);

  // 优化：先获取不带封面的画板，当真实需要渲染时再请求画板展示
  // 带封面画板
  const [coverBoards, setCoverBoards] = useSetState({});
  // 更新封面画板
  const updateRef = useRef({
    timer: null,
    ids: new Set(),
  });
  const updateCovers = useMemoizedFn((id) => {
    updateRef.current.ids.add(id);
    if (updateRef.current?.timer) {
      return;
    }

    updateRef.current.timer = setTimeout(async () => {
      const ids = [...updateRef.current?.ids];
      updateRef.current.ids.clear();
      const { boards, err } = await getBoardsListByIds(ids, 1);
      if (err) {
        return;
      }

      const boardsMap = {};
      boards.forEach((board) => {
        boardsMap[board?.board_id] = board;
      });

      setCoverBoards(boardsMap);

      updateRef.current.timer = null;
    }, 0);
  });
  const getBoardCover = useMemoizedFn((board) => {
    const boardId = board.board_id;
    if (coverBoards[boardId]) {
      return (
        coverBoards[boardId]?.cover?.file ||
        (coverBoards[boardId]?.pins || [])[0]?.file
      );
    }

    updateCovers(boardId);
    return null;
  });

  const renderItem = useMemoizedFn((board, index) => {
    const start = renderIndex;
    const end = start + RENDER_COUNT;
    const coverFile = getBoardCover(board);

    if (index < start || index > end) {
      return null;
    }

    return (
      <PinCreatorBoardCard
        board={board}
        key={board.board_id}
        index={index}
        activeIndex={activeIndex}
        onSelect={onSelectBoard}
        cover={coverFile}
      />
    );
  });

  useKeyPress("enter", async () => {
    const editing = isEditingElement();
    if (editing) {
      return false;
    }

    const board = dataSource[activeIndex];
    if (board) {
      onSelectBoard(board.board_id);
    }
  });

  useKeyPress("uparrow", (evt) => {
    // const editing = isEditingElement();
    // if (editing) {
    //   return false;
    // }

    evt.preventDefault();

    const index = activeIndex <= 0 ? 0 : activeIndex - 1;
    setActiveIndex(index);

    const target = listRef.current.querySelector(`[data-index="${index}"]`);

    target?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  });

  useKeyPress("downarrow", (evt) => {
    // const editing = isEditingElement();
    // if (editing) {
    //   return false;
    // }

    evt.preventDefault();

    const index =
      activeIndex >= dataSource.length - 1
        ? dataSource.length - 1
        : activeIndex + 1;
    setActiveIndex(index);

    const target = listRef.current.querySelector(`[data-index="${index}"]`);

    target?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  });

  const onCreate = useCallback(() => {
    listRef.current.scrollTo(0, 0);
  }, []);

  useUpdateEffect(() => {
    keyword && listRef.current?.scrollTo(0, 0);
  }, [keyword]);

  return (
    <div className={styles.listContainer}>
      <div className={styles.listWrapper}>
        <div className={styles.list} onScroll={onScroll} ref={listRef}>
          <If condition={dataSource.length > 0}>
            <Then>
              <div
                key={"before"}
                style={{ height: renderIndex * ITEM_HEIGHT }}
              ></div>
              {dataSource.map(renderItem)}
              <div
                key={"after"}
                style={{
                  height:
                    dataSource.length > renderIndex + RENDER_COUNT
                      ? (dataSource.length - (renderIndex + RENDER_COUNT)) *
                        ITEM_HEIGHT
                      : 0,
                }}
              ></div>
            </Then>
            <Else>
              {!keyword && (
                <PinCreatorBoardCard
                  board={null}
                  operationVisible={true}
                  onSelect={onSelectBoard}
                />
              )}
            </Else>
          </If>
        </div>
      </div>
      <div className={styles.create}>
        <div className={styles.split}></div>
        <CreateBoard
          className={styles.createBoard}
          onCreate={onCreate}
          boardName={keyword}
        />
      </div>
    </div>
  );
}
